// Cord figures in the app's pages: a Continue card's progress cord, a
// work's cord, a profile's strings, a clothesline of covers, and Home's
// year, woven. Each figure draws on its own canvas laid over its host
// element, and one frame loop runs every figure on screen. The cords and
// knots are the Living Khipu's (src/landing/cords.ts and knots.ts), drawn
// the same way. Every cord is held at both ends: nothing hangs.
//
// A figure's builder says, for the host's size, which cords to hang where
// and which knots to tie on them. Laying it out again (the host resized, the
// data behind it changed) moves the cords there and ties or unties whatever
// changed, always at the start of a frame, before it's drawn: resizing a
// canvas clears it. With reduced motion the cords don't sway, knots jump to
// their new state and every layout is settled at rest.
import {
  Cord,
  KnotRun,
  N,
  Sim,
  clamp,
  frameAt,
  layCord,
  makeCord,
  measureCord,
  nearest,
  pointAt,
  step,
} from "../landing/cords";
import {
  drawBeads,
  drawKnot,
  drawPly,
  strokeCased,
  traceCord,
} from "../landing/draw";
import { planKnot, tickKnots } from "../landing/knots";
import { S, prefersReducedMotion } from "../lib/knot";

/** A colour: a CSS custom property read off the host, e.g. "--dye-tv". */
export type Dye = `--${string}`;

/** A point in the figure. */
export type PinSpec = { x: number; y: number; dir?: number };

/**
 * What a knot or stripe says when it's pointed at, and where clicking it
 * goes. One that stands for several things lists them, each its own link.
 */
export type KnotLabel = {
  title: string;
  detail: string;
  href?: string;
  items?: KnotLabel[];
};

export type KnotSpec = {
  id: string;
  /** Where on the cord, 0..1. */
  s: number;
  /** Turns. */
  n: number;
  /** Turns wound on (default all of them), and how tight (default 1). */
  p?: number;
  c?: number;
  /** Wait this long (ms) before tying it. */
  delay?: number;
  /** New turns wind on at one end rather than round the middle. */
  grow?: boolean;
  /** Size (default from the cord's width). */
  k?: number;
  /** Its own colour rather than the cord's. */
  dye?: Dye;
  /** Fades it (with its cord's own), 0..1. */
  alpha?: number;
  label?: KnotLabel;
};

/** A title woven into a band: its picks, the threads across the band. */
export type StripeSpec = {
  id: string;
  /** Where each pick crosses the band, 0..1 along the cord, in order. */
  at: number[];
  /** Each pick's colour. */
  dyes: Dye[];
  /** How wide its picks are, px. */
  pick: number;
  /** Wait this long (ms) before weaving it in. */
  delay?: number;
  label?: KnotLabel;
};

/** A band woven on the cord in plain weave, drawn in place of the cord. */
export type BandSpec = {
  /** How wide it is across, px. */
  width: number;
  /** The warp: threads in the cord's colour, running along it. */
  warps: number;
  stripes: StripeSpec[];
  /** From here on (0..1 along the cord) the warp is faint: time to come. */
  fade?: number;
};

export type CordSpec = {
  id: string;
  dye: Dye;
  w: number;
  len: number;
  /** Where the cord is held, keyed by how far along it (0..1). */
  pins: Record<string, PinSpec>;
  knots?: KnotSpec[];
  /** Woven: a band on the cord instead of the cord itself. */
  band?: BandSpec;
  /** Plied with a second strand in this dye: two things together. */
  ply?: Dye;
  /** Beads run along it: something under way (an import coming in). */
  beads?: boolean;
  alpha?: number;
  damp?: number;
  /** Heavier points, [where, mass], carry what hangs from them. */
  heavy?: [number, number][];
  /** How far it may swing off the line between its pins, px. */
  reach?: number;
  /** How hard the air sways it (default 1). */
  sway?: number;
  /**
   * How heavy it hangs (default 1). A cord has as many points however long
   * it is, so one run across a page sags far under its own weight: a light
   * one runs level.
   */
  weight?: number;
};

export type FigureSpec = { cords: CordSpec[] };

/** Lays a figure out `W` by `H`: the host, plus its bleed on every side. */
export type Build = (W: number, H: number) => FigureSpec;

export type FigureOptions = {
  /** The pointer brushes the cords aside (default true). */
  push?: boolean;
  /** A mouse can grab a cord and swing it (default true). */
  grab?: boolean;
  /** How far past the host the canvas reaches on every side, px. */
  bleed?: number;
  /** Shows the label of a knot pointed at, or tapped, in this element. */
  tip?: HTMLElement;
  /** Follows a label's link: clicking it, or a stripe with one. */
  open?: (href: string) => void;
};

export type Figure = {
  /** Lay the figure out again, with a new builder if the data changed. */
  relayout: (build?: Build) => void;
  stop: () => void;
};

/** Something hung on a cord: `data-at="cord:where:mode"`. */
type Hung = {
  el: HTMLElement;
  cord: string;
  s: number;
  /** peg: pegged to the cord, tilting as it swings (a clothesline cover). */
  peg: boolean;
  w: number;
};

/** A stripe woven into a band, and when its first pick goes in. */
type Woven = { spec: StripeSpec; t0: number };

/**
 * Something labelled, where it was last drawn: `along` and `across` are how
 * far it reaches from there, along the cord and across it (a stripe spans
 * its band; a knot is a point).
 */
type Spot = {
  x: number;
  y: number;
  ang: number;
  along: number;
  across: number;
  label: KnotLabel;
};

type Fig = {
  host: HTMLElement;
  cv: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  build: Build;
  o: FigureOptions;
  W: number;
  H: number;
  dpr: number;
  vis: boolean;
  stale: boolean;
  sim: Sim;
  cords: Map<string, Cord>;
  specs: Map<Cord, CordSpec>;
  knots: WeakMap<KnotRun, KnotSpec>;
  /** Each band's stripes, by cord and stripe id. */
  woven: Map<string, Woven>;
  /** Where each labelled knot and stripe was last drawn. */
  spots: Map<KnotRun | Woven, Spot>;
  /** How big each knot is: the one pointed at swells. */
  swell: WeakMap<KnotRun, number>;
  hung: Hung[];
  colors: Map<string, string>;
  hot: KnotRun | Woven | null;
  /** Shown till a tap or click elsewhere: tapped, or a list clicked open. */
  held: KnotRun | Woven | null;
  /** A tap or click since the last frame, to look for a label under. */
  tap: Tap | null;
  /** Where the label points, in the figure: the top of what it labels. */
  tipAt: { x: number; y: number } | null;
};

/** A tap or click: where, when, whether it was a finger, and on what. */
type Tap = {
  x: number;
  y: number;
  t: number;
  touch: boolean;
  el: EventTarget | null;
};

// The pointer grabs cords, not links, buttons or what hangs on the cords.
const INTERACTIVE = "a,button,input,select,label,textarea,[data-at]";

const figs = new Set<Fig>();
const byHost = new Map<Element, Fig>();
const pointer = {
  x: -1e4,
  y: -1e4,
  vx: 0,
  vy: 0,
  on: false,
  touch: false,
  /** What it's over. */
  el: null as EventTarget | null,
};
let drag: { fig: Fig; c: Cord; k: number } | null = null;
// Where the pointer went down, while it may yet be a tap rather than a
// drag or a scroll.
let press: { x: number; y: number } | null = null;
let raf = 0;
let last = 0;
let seq = 0;
let seen: IntersectionObserver | null = null;
let sized: ResizeObserver | null = null;
let themes: MutationObserver | null = null;

// A dye's colour, read off the host once. One that reads as nothing (the
// page's stylesheet hasn't arrived: a browser may draw first) is grey for
// now and read again next time, rather than grey for good.
const color = (f: Fig, dye: Dye) => {
  let c = f.colors.get(dye);
  if (c === undefined) {
    c = getComputedStyle(f.host).getPropertyValue(dye).trim();
    if (!c) return "#888";
    f.colors.set(dye, c);
  }
  return c;
};

// Aim every pin at its point.
const aim = (f: Fig, c: Cord) => {
  const spec = f.specs.get(c)!;
  for (const key in c.pins) {
    c.pins[key].tx = spec.pins[key].x;
    c.pins[key].ty = spec.pins[key].y;
  }
};

// Tie the knots a cord should have, and untie the ones it no longer should.
const tie = (
  f: Fig,
  c: Cord,
  want: KnotSpec[],
  instant: boolean,
  now: number,
) => {
  const ids = new Set(want.map((w) => w.id));
  for (const kn of c.knots.values()) {
    if (ids.has(kn.id) || kn.dying) continue;
    kn.dying = true;
    planKnot(kn, 0, 0, 0, instant, now);
  }
  for (const w of want) {
    let kn = c.knots.get(w.id);
    if (!kn) {
      kn = {
        id: w.id,
        st: S(0, 0),
        cyc: null,
        t0: now,
        n: w.n,
        s: w.s,
        k: 0,
        kk: 1,
        spanPx: 0,
        top: false,
        grow: false,
        dying: false,
      };
      c.knots.set(w.id, kn);
    }
    kn.dying = false;
    kn.s = w.s;
    kn.k = w.k ?? 0;
    kn.grow = !!w.grow;
    // Never fewer turns than are wound on right now.
    kn.n = Math.max(w.n, Math.ceil(kn.st.p - 1e-6));
    f.knots.set(kn, w);
    planKnot(kn, w.p ?? w.n, w.c ?? 1, w.delay ?? 0, instant, now);
  }
};

const settle = (f: Fig) => {
  for (let s = 0; s < 200; s++) {
    for (const c of f.sim.cords) {
      measureCord(c);
      aim(f, c);
    }
    step(f.sim, 1 / 60, 0);
  }
  for (const c of f.sim.cords) measureCord(c);
};

const layout = (f: Fig) => {
  const r = f.host.getBoundingClientRect();
  // Not laid out (hidden, or not in the page yet): try again when it is.
  if (r.width < 2 || r.height < 2) {
    f.stale = true;
    return;
  }
  f.stale = false;
  const b = f.o.bleed ?? 0;
  f.W = r.width + 2 * b;
  f.H = r.height + 2 * b;
  f.dpr = Math.min(window.devicePixelRatio || 1, 2);
  Object.assign(f.cv.style, {
    left: `${-b}px`,
    top: `${-b}px`,
    width: `${f.W}px`,
    height: `${f.H}px`,
  });
  // Sizing a canvas clears it, even to the size it already is.
  const w = Math.round(f.W * f.dpr);
  const h = Math.round(f.H * f.dpr);
  if (f.cv.width !== w) f.cv.width = w;
  if (f.cv.height !== h) f.cv.height = h;
  f.sim.M = window.innerWidth < 760;
  f.hung = [...f.host.querySelectorAll<HTMLElement>("[data-at]")].map((el) => {
    const [cord, where, mode] = el.dataset.at!.split(":");
    return {
      el,
      cord,
      s: parseFloat(where) || 0,
      peg: mode === "peg",
      w: el.offsetWidth,
    };
  });

  const now = performance.now();
  const spec = f.build(f.W, f.H);
  const fresh = new Set<Cord>();
  const woven = new Set<string>();
  const ids = new Set(spec.cords.map((cs) => cs.id));
  for (const [id, c] of f.cords) {
    if (ids.has(id)) continue;
    f.cords.delete(id);
    f.specs.delete(c);
  }
  for (const cs of spec.cords) {
    let c = f.cords.get(cs.id);
    if (!c) {
      c = makeCord(seq++, "natural");
      f.cords.set(cs.id, c);
      fresh.add(c);
    }
    f.specs.set(c, cs);
    c.len = cs.len;
    c.w = cs.w;
    c.alpha = cs.alpha ?? 1;
    c.damp = cs.damp ?? 0;
    c.reach = cs.reach ?? Infinity;
    c.sway = cs.sway ?? 1;
    c.weight = cs.weight ?? 1;
    c.m.fill(1);
    for (const [s, mass] of cs.heavy ?? [])
      c.m[Math.round(clamp(s, 0, 1) * (N - 1))] = mass;
    for (const key in c.pins) if (!(key in cs.pins)) delete c.pins[key];
    for (const key in cs.pins) {
      if (!c.pins[key]) {
        const idx = clamp(Math.round(parseFloat(key) * (N - 1)), 0, N - 1);
        c.pins[key] = { idx, x: c.x[idx], y: c.y[idx], tx: 0, ty: 0, age: 0 };
      }
      c.pins[key].dir = cs.pins[key].dir;
    }
    tie(f, c, cs.knots ?? [], f.sim.reduce, now);
    for (const st of cs.band?.stripes ?? []) {
      const key = `${cs.id}:${st.id}`;
      woven.add(key);
      const w = f.woven.get(key);
      if (w) w.spec = st;
      else
        f.woven.set(key, {
          spec: st,
          t0: f.sim.reduce ? -Infinity : now + (st.delay ?? 0),
        });
    }
  }
  for (const key of f.woven.keys()) if (!woven.has(key)) f.woven.delete(key);
  // Drawn in the build's order, whichever cords came first: one added on a
  // relayout mustn't land on top of the one the build draws last, as a
  // splice's knot is when a guide brings more strands than the last.
  f.sim.cords = spec.cords.map((cs) => f.cords.get(cs.id)!);
  // New cords start where they belong rather than falling in.
  for (const c of f.sim.cords) {
    if (fresh.has(c)) {
      aim(f, c);
      layCord(c, f.W);
    }
    measureCord(c);
  }
  if (f.sim.reduce) settle(f);
};

// The labelled knot or stripe nearest (x, y) in a figure, within `r` px of
// it: of a knot's middle, or of anywhere a stripe crosses its band.
const spotNear = (f: Fig, x: number, y: number, r: number) => {
  let best: KnotRun | Woven | null = null;
  let bd = r;
  for (const [at, s] of f.spots) {
    const dx = x - s.x;
    const dy = y - s.y;
    const cos = Math.cos(s.ang);
    const sin = Math.sin(s.ang);
    const d = Math.hypot(
      Math.max(0, Math.abs(dx * cos + dy * sin) - s.along),
      Math.max(0, Math.abs(dy * cos - dx * sin) - s.across),
    );
    if (d < bd) {
      bd = d;
      best = at;
    }
  }
  return best;
};

// Whether the pointer is on a figure's label, or crossing the gap down to
// what it labels, so it can reach the label's links.
const onTip = (tip: HTMLElement) => {
  const b = tip.getBoundingClientRect();
  return (
    pointer.x >= b.left &&
    pointer.x <= b.right &&
    pointer.y >= b.top &&
    pointer.y <= b.bottom + 24
  );
};

// Whether something over the figure has the pointer, or had a tap: a work's
// modal, opened over the page from a label's link, or the bottom nav, fixed
// over the page as it scrolls.
const covered = (f: Fig, el = pointer.el) =>
  el instanceof Node &&
  !el.contains(f.host) &&
  !f.host.contains(el) &&
  !f.o.tip!.contains(el);

// A label's line: its title over its detail, a link where it has one.
const tipLine = (label: KnotLabel) => {
  const el = document.createElement(label.href ? "a" : "span");
  if (label.href) {
    el.setAttribute("href", label.href);
    // The page says it all for a screen reader or a keyboard: the tip is
    // for the pointer.
    el.tabIndex = -1;
  }
  const small = document.createElement("small");
  small.textContent = label.detail;
  el.append(label.title, small);
  return el;
};

// Write a label into the tip: its line, then each thing it stands for.
const fillTip = (tip: HTMLElement, label: KnotLabel) => {
  if (!label.items) return tip.replaceChildren(tipLine(label));
  const list = document.createElement("ul");
  for (const item of label.items) {
    const li = document.createElement("li");
    li.append(tipLine(item));
    list.append(li);
  }
  tip.replaceChildren(tipLine(label), list);
};

// Follow a label's link, putting the label away.
const follow = (f: Fig, href: string) => {
  f.held = f.hot = null;
  f.o.tip!.classList.remove("on");
  // Whatever opens isn't the figure: point again before it reads anything.
  pointer.on = false;
  f.o.open!(href);
};

// How far what a label points at moves before the label follows it (px).
const TIP_SLACK = 4;

// Point at a labelled knot or stripe to read it. A tap holds its label, as
// a finger lifts off; a click follows its link, or holds a label that lists
// several, till a tap or a click elsewhere.
const point = (f: Fig, r: DOMRect, now: number) => {
  const tip = f.o.tip!;
  const mouse = pointer.on && !pointer.touch && !covered(f);
  let hot = !mouse
    ? null
    : f.hot && onTip(tip)
      ? f.hot
      : spotNear(f, pointer.x - r.left, pointer.y - r.top, 18);
  const tap = f.tap;
  f.tap = null;
  if (tap && now - tap.t < 500) {
    // One on what covers the figure is that thing's, and lets go of a label
    // held, rather than holding the one under it over what was tapped.
    const at = covered(f, tap.el)
      ? null
      : spotNear(f, tap.x - r.left, tap.y - r.top, 22);
    const label = at && f.spots.get(at)!.label;
    f.held = tap.touch || label?.items ? at : null;
    if (!tap.touch && label?.href && !label.items && f.o.open)
      return follow(f, label.href);
    if (f.held) hot = f.held;
  }
  if (!hot && f.held && f.spots.has(f.held)) hot = f.held;
  f.host.style.cursor = hot && !pointer.touch ? "pointer" : "";
  const changed = hot !== f.hot;
  if (changed) {
    f.hot = hot;
    if (hot) fillTip(tip, f.spots.get(hot)!.label);
    tip.classList.toggle("on", !!hot);
  }
  if (hot) {
    const at = f.spots.get(hot)!;
    // Just above it: above a stripe's band, too. A cord stepped on uneven
    // frames trembles a pixel or three, and a label following it bounces,
    // so it stays put till what it labels has really moved.
    const to = { x: at.x, y: at.y - at.across };
    const was = f.tipAt;
    if (
      !was ||
      changed ||
      Math.abs(to.x - was.x) > TIP_SLACK ||
      Math.abs(to.y - was.y) > TIP_SLACK
    )
      f.tipAt = to;
    const half = tip.offsetWidth / 2 + 8;
    // Placed on the page, not the window, so it scrolls with the page
    // rather than a frame behind it.
    const x = clamp(r.left + f.tipAt!.x, half, window.innerWidth - half);
    tip.style.left = `${Math.round(x + window.scrollX)}px`;
    tip.style.top = `${Math.round(r.top + f.tipAt!.y - 18 + window.scrollY)}px`;
  }
};

// A pick goes in this long after the one before it (ms).
const PICK_MS = 40;
// How faint the warp is where nothing's woven yet.
const FAINT = 0.32;

// A line across a band.
type Line = { x0: number; y0: number; x1: number; y1: number };

// `items` in groups, by `key`.
const groupBy = <T>(items: T[], key: (t: T) => string) => {
  const groups = new Map<string, T[]>();
  for (const t of items) {
    const g = groups.get(key(t));
    if (g) g.push(t);
    else groups.set(key(t), [t]);
  }
  return [...groups.values()];
};

const traceLines = (x: CanvasRenderingContext2D, lines: Line[]) => {
  x.beginPath();
  for (const l of lines) {
    x.moveTo(l.x0, l.y0);
    x.lineTo(l.x1, l.y1);
  }
};

// A band in plain weave: the warp runs along the cord, and each pick
// crosses it over one warp thread and under the next, the next pick the
// other way. A stripe's picks go in one by one; a stripe pointed at stands
// out past the band's edges. However many picks there are, it's a few
// strokes: the warp, the picks a width and colour at a time, and the warp
// over them again.
const weave = (f: Fig, c: Cord, cs: CordSpec, now: number) => {
  const x = f.ctx;
  const band = cs.band!;
  const half = band.width / 2;
  const K = band.warps;
  const ww = Math.max(1.2, (band.width / K) * 0.55);
  const warp = color(f, cs.dye);
  // How far warp thread `i` is from the cord, across the band.
  const off = (i: number) => ((i + 0.5) / K - 0.5) * band.width;
  const steps = clamp(Math.ceil(c.total / 5), 12, 500);
  const along = Array.from({ length: steps + 1 }, (_, q) =>
    frameAt(c, q / steps, 4),
  );
  const warps = (alpha: number) => {
    x.beginPath();
    for (let i = 0; i < K; i++) {
      const o = off(i);
      along.forEach((p, q) => {
        const px = p.x - Math.sin(p.ang) * o;
        const py = p.y + Math.cos(p.ang) * o;
        if (q) x.lineTo(px, py);
        else x.moveTo(px, py);
      });
    }
    strokeCased(x, ww, warp, alpha * c.alpha);
  };
  if (band.fade === undefined) warps(1);
  else {
    const fx = pointAt(c, band.fade).x;
    for (const [x0, x1, alpha] of [
      [-80, fx, 1],
      [fx, f.W + 80, FAINT],
    ]) {
      x.save();
      x.beginPath();
      x.rect(x0, -80, x1 - x0, f.H + 160);
      x.clip();
      warps(alpha);
      x.restore();
    }
  }

  // Every pick along the band, in order, so each one's over-and-under is
  // the same whichever have gone in yet.
  const picks = band.stripes
    .flatMap((spec) => {
      const w = f.woven.get(`${cs.id}:${spec.id}`)!;
      return spec.at.map((u, j) => ({ u, dye: spec.dyes[j], w, j }));
    })
    .sort((a, b) => a.u - b.u)
    .map((p, n) => ({ ...p, over: n % 2 }))
    .filter((p) => now >= p.w.t0 + p.j * PICK_MS)
    .map((p) => {
      const q = frameAt(c, p.u, 4);
      const nx = -Math.sin(q.ang);
      const ny = Math.cos(q.ang);
      const h = half + ww * 0.35 + (p.w === f.hot ? 4 : 0);
      return {
        ...p,
        q,
        pw: p.w.spec.pick,
        col: color(f, p.dye),
        x0: q.x - nx * h,
        y0: q.y - ny * h,
        x1: q.x + nx * h,
        y1: q.y + ny * h,
      };
    });
  // A pick's gap: how far the warp beside it is cut back.
  const gap = (pw: number) => Math.max(0.5, pw * 0.22);
  x.lineCap = "butt";
  x.globalAlpha = c.alpha;
  x.globalCompositeOperation = "destination-out";
  x.strokeStyle = "#000";
  for (const g of groupBy(picks, (p) => `${p.pw}`)) {
    traceLines(x, g);
    x.lineWidth = g[0].pw + 2 * gap(g[0].pw);
    x.stroke();
  }
  x.globalCompositeOperation = "source-over";
  for (const g of groupBy(picks, (p) => `${p.pw} ${p.col}`)) {
    traceLines(x, g);
    x.strokeStyle = g[0].col;
    x.lineWidth = g[0].pw;
    x.stroke();
  }
  // Then the warp threads each pick passes under, over it again: unbroken
  // through the pick's gap, so the thread runs on, with a shade on the pick
  // either side of it where the pick dips under. Cut and capped, each
  // crossing would break the pick into separate scraps.
  const over: Line[] = [];
  const shade: Line[] = [];
  for (const { q, pw, over: first } of picks) {
    const cos = Math.cos(q.ang);
    const sin = Math.sin(q.ang);
    const d = pw * 0.5 + gap(pw);
    for (let i = first; i < K; i += 2) {
      const wx = q.x - sin * off(i);
      const wy = q.y + cos * off(i);
      over.push({
        x0: wx - cos * d,
        y0: wy - sin * d,
        x1: wx + cos * d,
        y1: wy + sin * d,
      });
      shade.push({
        x0: wx - (cos * pw) / 2,
        y0: wy - (sin * pw) / 2,
        x1: wx + (cos * pw) / 2,
        y1: wy + (sin * pw) / 2,
      });
    }
  }
  traceLines(x, shade);
  x.globalAlpha = c.alpha * 0.28;
  x.strokeStyle = "#000";
  x.lineWidth = ww + 2 * Math.min(1.2, ww * 0.45);
  x.stroke();
  traceLines(x, over);
  x.globalAlpha = c.alpha;
  x.strokeStyle = warp;
  x.lineWidth = ww;
  x.stroke();
  x.lineCap = "round";

  // Where each labelled stripe is, once it's begun to go in.
  for (const spec of band.stripes) {
    const w = f.woven.get(`${cs.id}:${spec.id}`)!;
    if (!spec.label || now < w.t0) continue;
    const u0 = spec.at[0];
    const u1 = spec.at[spec.at.length - 1];
    const q = frameAt(c, (u0 + u1) / 2, 4);
    f.spots.set(w, {
      x: q.x,
      y: q.y,
      ang: q.ang,
      along: ((u1 - u0) / 2) * c.total + spec.pick / 2,
      across: half,
      label: spec.label,
    });
  }
};

const draw = (f: Fig, now: number) => {
  const x = f.ctx;
  x.setTransform(f.dpr, 0, 0, f.dpr, 0, 0);
  x.clearRect(0, 0, f.W, f.H);
  x.lineCap = "round";
  x.lineJoin = "round";
  f.spots.clear();
  for (const c of f.sim.cords) {
    if (c.alpha < 0.01) continue;
    const spec = f.specs.get(c)!;
    if (spec.band) {
      weave(f, c, spec, now);
      continue;
    }
    const col = color(f, spec.dye);
    // Still, unlike the landing's: a ply turning on its own would be a
    // motion for its own sake.
    if (spec.ply) drawPly(x, c, col, color(f, spec.ply), c.alpha, 0);
    else {
      traceCord(x, c);
      strokeCased(x, c.w, col, c.alpha);
    }
    // With reduced motion they stand still, where they are at the start.
    if (spec.beads) drawBeads(x, c, f.sim.reduce ? 0 : now / 1000, c.alpha);
    for (const kn of c.knots.values()) {
      const at = frameAt(c, kn.s, Math.max(4, kn.spanPx * 0.5));
      const ks = f.knots.get(kn);
      if (ks?.label && kn.st.p >= 1)
        f.spots.set(kn, { ...at, along: 0, across: 0, label: ks.label });
      const was = f.swell.get(kn) ?? 1;
      const hs = was + ((kn === f.hot ? 1.4 : 1) - was) * 0.35;
      f.swell.set(kn, hs);
      kn.kk *= hs;
      drawKnot(
        x,
        at.x,
        at.y,
        at.ang,
        ks?.dye ? color(f, ks.dye) : col,
        kn,
        c.alpha * (ks?.alpha ?? 1),
      );
    }
  }
  x.globalAlpha = 1;
  x.globalCompositeOperation = "source-over";
  x.setLineDash([]);
};

// Move what hangs on the cords to where the cords are now.
const place = (f: Fig) => {
  const b = f.o.bleed ?? 0;
  for (const a of f.hung) {
    const c = f.cords.get(a.cord);
    if (!c) continue;
    const at = frameAt(c, a.s, 10);
    // A pegged cover tilts with the line, and swings as it moves.
    const rot = a.peg
      ? clamp(at.ang * 0.3 - (c.x[at.k] - c.px[at.k]) * 0.06, -0.4, 0.4)
      : 0;
    a.el.style.transform = `translate3d(${(at.x - b - a.w / 2).toFixed(1)}px,${(at.y - b).toFixed(1)}px,0) rotate(${rot.toFixed(3)}rad)`;
  }
};

const frame = (now: number) => {
  raf = 0;
  const dt = clamp((now - last) / 1000, 0.001, 0.034);
  last = now;
  const time = now / 1000;
  let shown = false;
  for (const f of figs) {
    if (!f.vis) continue;
    shown = true;
    if (f.stale) layout(f);
    if (f.stale) continue;
    const r = f.cv.getBoundingClientRect();
    const held = drag?.fig === f ? drag : null;
    const ptr = f.sim.ptr;
    ptr.x = pointer.x - r.left;
    ptr.y = pointer.y - r.top;
    ptr.vx = pointer.vx;
    ptr.vy = pointer.vy;
    ptr.touch = pointer.touch;
    ptr.on = !!held || (pointer.on && f.o.push !== false);
    f.sim.drag = held && { c: held.c, k: held.k };
    for (const c of f.sim.cords) measureCord(c);
    for (const c of f.sim.cords) aim(f, c);
    step(f.sim, dt / 2, time);
    step(f.sim, dt / 2, time);
    for (const c of f.sim.cords) measureCord(c);
    tickKnots(f.sim.cords, now);
    if (f.o.tip) point(f, r, now);
    draw(f, now);
    place(f);
  }
  pointer.vx *= 0.6;
  pointer.vy *= 0.6;
  // Nothing on screen: sleep until something scrolls into view.
  if (shown) raf = requestAnimationFrame(frame);
};

const wake = () => {
  if (raf || !figs.size) return;
  last = performance.now();
  raf = requestAnimationFrame(frame);
};

// The cord point nearest (x, y) on the page, within `r` px, among the
// figures on screen that let a cord be grabbed.
const hit = (x: number, y: number, r: number) => {
  for (const f of figs) {
    if (!f.vis || f.stale || f.o.grab === false) continue;
    const box = f.cv.getBoundingClientRect();
    const got = nearest(f.sim.cords, x - box.left, y - box.top, r);
    if (got) return { fig: f, ...got };
  }
  return null;
};

const onMove = (e: PointerEvent) => {
  if (pointer.on) {
    pointer.vx = e.clientX - pointer.x;
    pointer.vy = e.clientY - pointer.y;
  }
  pointer.x = e.clientX;
  pointer.y = e.clientY;
  pointer.on = true;
  pointer.touch = e.pointerType === "touch";
  pointer.el = e.target;
  if (!drag && e.pointerType === "mouse") {
    const over = (e.target as Element).closest?.(INTERACTIVE);
    document.documentElement.style.cursor =
      !over && hit(pointer.x, pointer.y, 8) ? "grab" : "";
  }
};

const onDown = (e: PointerEvent) => {
  if (e.button !== 0) return;
  pointer.x = e.clientX;
  pointer.y = e.clientY;
  pointer.vx = pointer.vy = 0;
  pointer.touch = e.pointerType === "touch";
  press = { x: e.clientX, y: e.clientY };
  // A finger scrolls the page: it only taps.
  if (pointer.touch) return;
  if ((e.target as Element).closest?.(INTERACTIVE)) return;
  const got = hit(pointer.x, pointer.y, 10);
  if (!got) return;
  pointer.on = true;
  drag = got;
  document.body.classList.add("grabbing");
  e.preventDefault();
};

const release = () => {
  if (drag) {
    // Let go with the pointer's momentum.
    drag.c.px[drag.k] = drag.c.x[drag.k] - pointer.vx * 0.6;
    drag.c.py[drag.k] = drag.c.y[drag.k] - pointer.vy * 0.6;
    drag = null;
  }
  document.body.classList.remove("grabbing");
};

const onUp = (e: PointerEvent) => {
  release();
  // Let go where it went down: a tap or a click, not a drag. One on a label
  // is the label's own (its links).
  const t = e.target;
  if (
    press &&
    Math.hypot(e.clientX - press.x, e.clientY - press.y) < 10 &&
    !(t instanceof Node && [...figs].some((f) => f.o.tip?.contains(t)))
  )
    for (const f of figs)
      f.tap = {
        x: e.clientX,
        y: e.clientY,
        t: performance.now(),
        touch: e.pointerType === "touch",
        el: t,
      };
  press = null;
  if (e.pointerType === "touch") pointer.on = false;
};

// Whatever else happens (a scroll takes the finger), it isn't a tap.
const onLeave = () => {
  release();
  press = null;
  pointer.on = false;
};

// A new size (the window, a scrollbar, fonts loading) lays every figure out
// again, in its next frame.
const onResize = () => {
  for (const f of figs) f.stale = true;
};

const onTheme = () => {
  for (const f of figs) f.colors.clear();
};

const listen = () => {
  window.addEventListener("pointermove", onMove, { passive: true });
  window.addEventListener("pointerdown", onDown);
  window.addEventListener("pointerup", onUp);
  window.addEventListener("pointercancel", onLeave);
  window.addEventListener("blur", onLeave);
  document.addEventListener("pointerleave", onLeave);
  window.addEventListener("resize", onResize);
  // An entry can still arrive for a figure that has just stopped.
  if (typeof IntersectionObserver !== "undefined")
    seen = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const f = byHost.get(e.target);
          if (f) f.vis = e.isIntersecting;
        }
        wake();
      },
      { rootMargin: "200px" },
    );
  if (typeof ResizeObserver !== "undefined")
    sized = new ResizeObserver((entries) => {
      for (const e of entries) {
        const f = byHost.get(e.target);
        if (f) f.stale = true;
      }
    });
  themes = new MutationObserver(onTheme);
  themes.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"],
  });
};

const unlisten = () => {
  window.removeEventListener("pointermove", onMove);
  window.removeEventListener("pointerdown", onDown);
  window.removeEventListener("pointerup", onUp);
  window.removeEventListener("pointercancel", onLeave);
  window.removeEventListener("blur", onLeave);
  document.removeEventListener("pointerleave", onLeave);
  window.removeEventListener("resize", onResize);
  seen?.disconnect();
  sized?.disconnect();
  themes!.disconnect();
  seen = sized = themes = null;
  cancelAnimationFrame(raf);
  raf = 0;
  release();
  pointer.on = false;
  pointer.el = null;
  press = null;
  document.documentElement.style.cursor = "";
};

/** Hang a figure's cords over `host`, or return null where canvas can't draw. */
export const startFigure = (
  host: HTMLElement,
  build: Build,
  options: FigureOptions = {},
): Figure | null => {
  const cv = document.createElement("canvas");
  const ctx = cv.getContext("2d");
  if (!ctx) return null;
  cv.className = "fig-canvas";
  cv.setAttribute("aria-hidden", "true");
  host.prepend(cv);
  host.classList.add("fig-live");
  const f: Fig = {
    host,
    cv,
    ctx,
    build,
    o: options,
    W: 0,
    H: 0,
    dpr: 1,
    vis: true,
    stale: true,
    sim: {
      cords: [],
      ptr: { ...pointer },
      drag: null,
      M: false,
      reduce: prefersReducedMotion(),
      mid: 0,
    },
    cords: new Map(),
    specs: new Map(),
    knots: new WeakMap(),
    woven: new Map(),
    spots: new Map(),
    swell: new WeakMap(),
    hung: [],
    colors: new Map(),
    hot: null,
    held: null,
    tap: null,
    tipAt: null,
  };
  // A label's links go where `open` takes them; a link opened in a new tab
  // goes as any other.
  const onTipClick = (e: MouseEvent) => {
    const a = (e.target as Element).closest("a");
    if (!a || !options.open || e.button || e.metaKey || e.ctrlKey || e.shiftKey)
      return;
    e.preventDefault();
    follow(f, a.getAttribute("href")!);
  };
  options.tip?.addEventListener("click", onTipClick);
  if (!figs.size) listen();
  figs.add(f);
  byHost.set(host, f);
  seen?.observe(host);
  sized?.observe(host);
  layout(f);
  wake();
  return {
    relayout: (next) => {
      if (next) f.build = next;
      f.stale = true;
      wake();
    },
    stop: () => {
      if (drag?.fig === f) release();
      figs.delete(f);
      byHost.delete(host);
      seen?.unobserve(host);
      sized?.unobserve(host);
      cv.remove();
      host.classList.remove("fig-live");
      host.style.cursor = "";
      for (const a of f.hung) a.el.style.transform = "";
      f.o.tip?.classList.remove("on");
      f.o.tip?.removeEventListener("click", onTipClick);
      if (!figs.size) unlisten();
    },
  };
};
